import { useCallback, useState } from 'react';
import { Feather, AlertCircle, Check } from 'lucide-react';
import { useResetPasswordMutation } from './api';

/**
 * Reached from the reset email link (`#/reset-password?token=…`). The token was
 * already validated implicitly — reset-password rejects a bad/expired one — so
 * this screen only asks for the new password.
 */
export function ResetPasswordScreen({ onDone }: { onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [reset, { isLoading }] = useResetPasswordMutation();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const token = (() => {
    try {
      return new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('token') ?? '';
    } catch {
      return '';
    }
  })();

  const onSubmit = useCallback(async () => {
    setError(null);
    if (password.length < 8) {
      setError('Passwords are at least 8 characters long');
      return;
    }
    if (password !== confirm) {
      setError('Those passwords do not match');
      return;
    }
    try {
      await reset({ newPassword: password, token }).unwrap();
      setDone(true);
    } catch (e) {
      const err = e as { data?: { message?: string | string[] }; status?: unknown };
      const message = Array.isArray(err.data?.message)
        ? err.data!.message[0]
        : err.data?.message;
      setError(
        message === 'Invalid token'
          ? 'This reset link is invalid or has expired — request a new one.'
          : message ?? 'Could not reset your password',
      );
    }
  }, [password, confirm, token, reset]);

  return (
    <main className="auth-page">
      <div className="auth-brand">
        <div className="auth-logo">
          <Feather size={26} />
        </div>
        <h1 className="auth-title">toiyp</h1>
        <p className="auth-tagline">letters, slowly</p>
      </div>

      <div className="auth-card">
        {done ? (
          <div className="auth-form" style={{ textAlign: 'center', paddingTop: 8 }}>
            <p className="form-success" role="status">
              <Check size={14} aria-hidden="true" />
              Your password is reset.
            </p>
            <p className="field__hint" style={{ margin: '12px 0 20px' }}>
              Sign in with your new password.
            </p>
            <button type="button" className="form-submit" onClick={onDone}>
              Go to sign in
            </button>
          </div>
        ) : (
          <form
            className="auth-form"
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
          >
            <p className="auth-intro">Choose a new password for your account.</p>

            <div className="field">
              <label className="field__label" htmlFor="reset-password">New password</label>
              <input
                id="reset-password"
                className="field__input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="at least 8 characters"
                required
                minLength={8}
                autoComplete="new-password"
                autoFocus
              />
            </div>

            <div className="field">
              <label className="field__label" htmlFor="reset-confirm">Confirm password</label>
              <input
                id="reset-confirm"
                className="field__input"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="again, for good measure"
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>

            {error && (
              <p className="form-error" role="alert">
                <AlertCircle size={14} aria-hidden="true" />
                {error}
              </p>
            )}

            <button type="submit" className="form-submit" disabled={isLoading}>
              {isLoading ? '…' : 'Reset password'}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}